import React, { useCallback } from "react";
import { useField } from "react-final-form";
import { $getRoot, InitialEditorStateType } from "lexical";
import { InitialConfigType, LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListItemNode, ListNode } from "@lexical/list";
import { LinkNode, AutoLinkNode } from "@lexical/link";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";

import ToolbarPlugin from "./plugins/toolbar-plugin";
import "./lexical-editor.css";

interface LexicalRichTextEditorProps {
  name: string;
  initialValue?: string;
}

// Removes inline `white-space` styles from inline elements
const sanitizeHTML = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  doc.body.querySelectorAll("*").forEach((el) => {
    const node = el as HTMLElement;
    // remove inline style attribute of all elements
    node.removeAttribute("style");
  });

  return doc.body.innerHTML;
};

// Component to handle editor change events
const ChangeHandler = ({ onChange }: { onChange: (html: string) => void }) => {
  const [editor] = useLexicalComposerContext();
  // sanitize on every update
  const handleChange = useCallback(() => {
    editor.update(() => {
      const htmlString = $generateHtmlFromNodes(editor, null);
      const sanitizedHtml = sanitizeHTML(htmlString);
      onChange(sanitizedHtml);
    });
  }, [editor, onChange]);

  return <OnChangePlugin onChange={handleChange} />;
};

const isLexicalStateString = (value: string): boolean => {
  if (!value) return false;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && "root" in parsed;
  } catch {
    return false;
  }
};

// Editor configuration
const editorConfig = {
  namespace: "KNMIEditor",
  theme: {
    root: "lexical-editor",
    text: {
      strikethrough: "lexical-text-strikethrough",
    },
  },
  nodes: [
    HeadingNode,
    QuoteNode,
    ListNode,
    ListItemNode,
    LinkNode,
    AutoLinkNode,
    TableNode,
    TableRowNode,
    TableCellNode,
  ],
  onError: (error: Error) => {
    console.error("Lexical Editor Error:", error);
  },
};

const LexicalRichTextEditor: React.FC<LexicalRichTextEditorProps> = ({
  name,
  initialValue = "",
}) => {
  // Register field
  const field = useField(name, {
    initialValue,
    type: "text",
  });

  const handleChange = useCallback(
    (html: string) => {
      field.input.onChange(html);
    },
    [field.input],
  );

  const initialEditorState: InitialEditorStateType = initialValue
    ? isLexicalStateString(initialValue)
      ? initialValue
      : (editor) => {
          const parser = new DOMParser();
          const dom = parser.parseFromString(initialValue, "text/html");
          const nodes = $generateNodesFromDOM(editor, dom);
          const root = $getRoot();
          root.clear();
          root.append(...nodes);
        }
    : null;

  const initialConfig: InitialConfigType = {
    ...editorConfig,
    editorState: initialEditorState,
  };

  return (
    <div className="lexical-editor-container">
      <LexicalComposer initialConfig={initialConfig}>
        <div className="lexical-editor-inner">
          <ToolbarPlugin />
          <div className="lexical-editor-input">
            <RichTextPlugin
              contentEditable={<ContentEditable className="lexical-content-editable" />}
              placeholder={<div className="lexical-placeholder">Enter some text...</div>}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <ChangeHandler onChange={handleChange} />
            <HistoryPlugin />
            <LinkPlugin />
            <ListPlugin />
            <TablePlugin />
          </div>
        </div>
      </LexicalComposer>
    </div>
  );
};

export default LexicalRichTextEditor;
